import { OptionProps } from '@mui/base';
import { Autocomplete, Checkbox, TextField, Typography } from '@mui/material';
import { DesktopDateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { AddExternalUserForm } from 'components/Drawer/types';
import dayjs from 'dayjs';
import { Role } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { upperFirst } from 'lodash';
import React from 'react';
import { Control, Controller, UseFormRegister } from 'react-hook-form';
import styled from 'styled-components';
import AutocompletePaper from 'ui/atoms/AutocompletePaper/AutocompletePaper';
import Icon from 'ui/atoms/Icon/Icon';
import Tooltip from 'ui/atoms/Tooltip/Tooltip';
import ComponentWrapper from 'ui/templates/ComponentWrapper/ComponentWrapper';

const IconWrapper = styled.div`
  cursor: pointer;
  align-items: center;
  height: 24px;
`;

export interface Props {
  register: UseFormRegister<AddExternalUserForm>;
  nameInputValue: string;
  handleNameInputOnChange: (value: string) => void;
  nameInputValidation: string | undefined;
  emailInputValue: string;
  handleEmailInputValueChange: (value: string) => void;
  emailInputValidation: string | undefined;
  switchIsAdministratorRoleEnabled: boolean | undefined;
  handleSwitchIsAdministratorRoleEnabledOnChange: (() => void) | undefined;
  switchIsExpirationDateEnabled: boolean | undefined;
  handleSwitchIsExpirationDateOnChange: () => void;
  inputExpirationDateToValue: dayjs.Dayjs | null;
  handleInputExpirationDateToValue: (value: dayjs.Dayjs | null) => void;
  inputExpirationDateToValueValidation: string | undefined;
  showValidation: boolean;
  control: Control<AddExternalUserForm, object>;
  selectValue: OptionProps<Role | null>;
  selectOption: OptionProps<Role | null>[];
  handleSelectChange: (value: OptionProps<Role> | null) => void;
  roleInputValidation: string | undefined;
  isMultiAdminEnabled: boolean;
}
const AddExternalUser: React.FC<React.PropsWithChildren<Props>> = ({
  register,
  nameInputValue,
  handleNameInputOnChange,
  nameInputValidation,
  emailInputValue,
  handleEmailInputValueChange,
  emailInputValidation,
  switchIsAdministratorRoleEnabled,
  handleSwitchIsAdministratorRoleEnabledOnChange,
  switchIsExpirationDateEnabled,
  handleSwitchIsExpirationDateOnChange,
  inputExpirationDateToValue,
  handleInputExpirationDateToValue,
  inputExpirationDateToValueValidation,
  showValidation,
  control,
  selectValue,
  selectOption,
  handleSelectChange,
  roleInputValidation,
  isMultiAdminEnabled
}) => {
  const { translation, getLanguage, getLocaleForDayjs } = useTranslation();

  return (
    <ComponentWrapper flexDirection="column" gap="1.5rem" margin="0.5rem 0 0 0">
      <TextField
        {...register('name')}
        onChange={(event) => handleNameInputOnChange(event.target.value)}
        variant="outlined"
        InputLabelProps={{ shrink: !!nameInputValue }}
        size="medium"
        label={translation.name}
        value={nameInputValue}
        id="drawer-input-name"
        error={showValidation && nameInputValidation !== undefined}
        helperText={showValidation ? nameInputValidation : undefined}
      />
      <TextField
        {...register('email')}
        onChange={(event) => handleEmailInputValueChange(event.target.value)}
        variant="outlined"
        InputLabelProps={{ shrink: !!emailInputValue }}
        size="medium"
        label={translation.e_mail}
        value={emailInputValue}
        id="drawer-input-email"
        error={showValidation && emailInputValidation !== undefined}
        helperText={showValidation ? emailInputValidation : undefined}
      />
      <ComponentWrapper justifyContent="space-between" alignItems="center" gap="8px">
        <Controller
          control={control}
          name="role"
          render={() => (
            <Autocomplete
              {...register('role')}
              options={selectOption}
              value={selectValue}
              onChange={(_, value) => {
                handleSelectChange(value as OptionProps<Role>);
              }}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              getOptionLabel={(option) => option.label as string}
              id="drawer-input-role"
              clearOnBlur={false}
              disableClearable
              fullWidth
              PaperComponent={AutocompletePaper}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={translation.role}
                  id="drawer-select-role-input-id"
                  variant="outlined"
                  size="medium"
                  fullWidth
                  InputLabelProps={{ shrink: !!selectValue?.value }}
                  error={showValidation && roleInputValidation !== undefined}
                  helperText={showValidation ? roleInputValidation : undefined}
                />
              )}
              sx={{
                '& .MuiSvgIcon-root': {
                  fill: 'white',
                  fillOpacity: 0.56
                }
              }}
            />
          )}
        />
        <Tooltip
          elementOnHover={
            <ComponentWrapper flexDirection="column" width="20rem" padding="1rem">
              <Typography variant="body1">
                {
                  translation.a_user_with_the_owner_role_has_the_ability_to_invite_other_users_to_this_group_up_to_maximum_user_count
                }
                .
              </Typography>
            </ComponentWrapper>
          }
          id="role-tooltip"
        >
          <IconWrapper>
            <Icon name="Info" id="locks-i-icon-role" />
          </IconWrapper>
        </Tooltip>
      </ComponentWrapper>
      {isMultiAdminEnabled && (
        <ComponentWrapper alignItems="center">
          <Checkbox
            {...register('isAdministratorRole')}
            color="primary"
            id="drawer-input-isAdministratorRole-checkbox"
            checked={switchIsAdministratorRoleEnabled}
            onClick={handleSwitchIsAdministratorRoleEnabledOnChange}
          />
          <Typography>{translation.administrator}</Typography>
        </ComponentWrapper>
      )}
      <ComponentWrapper alignItems="center">
        <Checkbox
          {...register('isOfficeModeEnabled')}
          id="drawer-input-expiration-date-checkbox"
          checked={switchIsExpirationDateEnabled}
          disabled={!!switchIsAdministratorRoleEnabled}
          onClick={handleSwitchIsExpirationDateOnChange}
        />
        <Typography>{translation.enable_expiration_date}</Typography>
      </ComponentWrapper>
      <Controller
        control={control}
        name="expirationDate"
        render={() => (
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={getLocaleForDayjs(getLanguage).name}>
            <DesktopDateTimePicker
              label={upperFirst(translation.expiration_date)}
              value={inputExpirationDateToValue}
              views={['year', 'day', 'hours', 'minutes']}
              onChange={handleInputExpirationDateToValue}
              disabled={!switchIsExpirationDateEnabled || switchIsAdministratorRoleEnabled}
              slotProps={{
                textField: {
                  helperText:
                    showValidation && !!inputExpirationDateToValueValidation
                      ? inputExpirationDateToValueValidation
                      : undefined,
                  id: 'drawer-input-expiration-date'
                }
              }}
            />
          </LocalizationProvider>
        )}
      />
    </ComponentWrapper>
  );
};

export default AddExternalUser;
